import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { RefreshCw, Send, Trash2, Sparkles, MessageSquare, Flag, AlertTriangle, CheckCircle2 } from "lucide-react";

const STATUSES = [
  { v: "", label: "Tutte" },
  { v: "pending", label: "In attesa" },
  { v: "drafted", label: "Bozze" },
  { v: "replied", label: "Risposte" },
  { v: "skipped", label: "Saltate" },
  { v: "failed", label: "Errori" },
];

export default function InboxPage() {
  const [emails, setEmails] = useState([]);
  const [filter, setFilter] = useState("");
  const [selected, setSelected] = useState(null);
  const [edit, setEdit] = useState({ ai_subject: "", ai_reply: "" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/emails${filter ? `?status=${filter}` : ""}`);
      setEmails(data);
      if (selected) {
        const updated = data.find((e) => e.id === selected.id);
        if (updated) {
          setSelected(updated);
          setEdit({ ai_subject: updated.ai_subject || "", ai_reply: updated.ai_reply || "" });
        }
      }
    } catch (err) {
      console.error("Inbox: failed to load /emails", err?.message || err);
    }
  };

  useEffect(() => {
    load();
    const i = setInterval(load, 15000);
    return () => clearInterval(i);
     
  }, [filter]); // eslint-disable-line react-hooks/exhaustive-deps

  const openEmail = (e) => {
    setSelected(e);
    setEdit({ ai_subject: e.ai_subject || `Re: ${e.subject}`, ai_reply: e.ai_reply || "" });
  };

  const regenerate = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/emails/${selected.id}/regenerate`);
      setEdit({ ai_subject: data.ai_subject, ai_reply: data.ai_reply });
      toast.success("Risposta rigenerata");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    } finally {
      setBusy(false);
    }
  };

  const saveDraft = async () => {
    if (!selected) return;
    try {
      await api.put(`/emails/${selected.id}`, edit);
      toast.success("Bozza salvata");
      load();
    } catch (err) {
      console.error("Inbox: saveDraft failed", err?.message || err);
      toast.error("Errore salvataggio");
    }
  };

  const send = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      await api.put(`/emails/${selected.id}`, edit);
      await api.post(`/emails/${selected.id}/approve`);
      toast.success("Email inviata");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore invio");
    } finally {
      setBusy(false);
    }
  };

  const skip = async () => {
    if (!selected) return;
    try {
      await api.post(`/emails/${selected.id}/skip`);
      toast.success("Email saltata");
      load();
    } catch (err) {
      console.error("Inbox: skip failed", err?.message || err);
      toast.error("Errore");
    }
  };

  const reflag = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/emails/${selected.id}/reflag`);
      if (data.system_ok && data.label_ok) {
        toast.success(`Flag applicata su IMAP (Stella + Risposto + "${data.label}")`);
      } else if (data.system_ok) {
        toast.success(`Flag stella+risposto OK. Label "${data.label}" non applicato: ${data.error || "rifiutato dal server"}`);
      } else {
        toast.error(`Flag fallita: ${data.error || "errore IMAP"}`);
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore IMAP");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="label-mono">Posta</div>
        <h1 className="font-display text-4xl mt-2 font-light">Inbox · Segretaria</h1>
      </div>

      <div className="flex flex-wrap gap-2">
        {STATUSES.map((s) => (
          <button
            key={s.v}
            onClick={() => setFilter(s.v)}
            data-testid={`filter-${s.v || "all"}`}
            className={`px-3 py-1.5 text-xs font-mono uppercase tracking-[0.15em] border transition-colors ${
              filter === s.v
                ? "border-[#2C4C3B] bg-[#2C4C3B] text-white"
                : "border-[#E5E4E0] bg-white text-[#5C5B5A] hover:bg-[#F0EFEA]"
            }`}
          >
            {s.label}
          </button>
        ))}
        <button
          onClick={load}
          data-testid="refresh-inbox-button"
          className="ml-auto px-3 py-1.5 text-xs font-mono uppercase tracking-[0.15em] border border-[#E5E4E0] bg-white text-[#5C5B5A] hover:bg-[#F0EFEA] flex items-center gap-1"
        >
          <RefreshCw size={12} /> Aggiorna
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 min-h-[500px]">
        {/* LIST */}
        <div className="lg:col-span-2 border border-[#E5E4E0] bg-white" data-testid="email-list">
          {emails.length === 0 ? (
            <div className="p-10 text-center text-sm text-[#8C8B8A]">
              Nessuna email{filter ? " in questo stato" : ""}.
            </div>
          ) : (
            <div className="divide-y divide-[#E5E4E0] max-h-[640px] overflow-y-auto">
              {emails.map((e) => (
                <button
                  key={e.id}
                  onClick={() => openEmail(e)}
                  data-testid={`email-row-${e.id}`}
                  className={`w-full text-left px-4 py-3 transition-colors ${
                    selected?.id === e.id ? "bg-[#F0EFEA]" : "hover:bg-[#F0EFEA]"
                  }`}
                >
                  <div className="flex items-center justify-between text-xs text-[#5C5B5A] gap-2">
                    <span className="truncate font-mono">{e.from_address}</span>
                    <span className="shrink-0">{new Date(e.received_at).toLocaleDateString("it-IT")}</span>
                  </div>
                  <div className="mt-1 text-sm truncate text-[#1C1B1A]">{e.subject}</div>
                  <div className="mt-1 flex items-center gap-1.5 flex-wrap">
                    <StatusBadge status={e.status} />
                    {e.status === "replied" && e.imap_flag_error && (
                      <span
                        data-testid={`flag-fail-${e.id}`}
                        title={`Flag IMAP fallita: ${e.imap_flag_error}`}
                        className="text-[9px] font-mono uppercase tracking-[0.15em] px-1.5 py-0.5 bg-[#FEE2E2] text-[#991B1B] flex items-center gap-1"
                      >
                        <Flag size={9} /> No-flag
                      </span>
                    )}
                    {e.conversation_count > 1 && (
                      <span
                        data-testid={`conv-badge-${e.id}`}
                        className="text-[9px] font-mono uppercase tracking-[0.15em] px-1.5 py-0.5 border border-[#2C4C3B] text-[#2C4C3B]"
                        title={`${e.conversation_count}° scambio con questo mittente`}
                      >
                        Thread {e.conversation_count}
                      </span>
                    )}
                    {e.detected_language && e.detected_language !== "it" && (
                      <span
                        data-testid={`lang-badge-${e.id}`}
                        className="text-[9px] font-mono uppercase tracking-[0.15em] px-1.5 py-0.5 bg-[#F0EFEA] text-[#5C5B5A]"
                      >
                        {e.detected_language.toUpperCase()}
                      </span>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* DETAIL */}
        <div className="lg:col-span-3 border border-[#E5E4E0] bg-white">
          {!selected ? (
            <div className="h-full flex flex-col items-center justify-center p-10 text-center text-[#8C8B8A] gap-3">
              <MessageSquare size={32} strokeWidth={1} />
              <p className="text-sm">Seleziona un&apos;email per visualizzare la risposta della Segretaria.</p>
            </div>
          ) : (
            <div className="p-6 space-y-5" data-testid="email-detail">
              <div className="border-b border-[#E5E4E0] pb-4">
                <div className="label-mono">Email originale</div>
                <h3 className="font-display text-2xl mt-1 font-light">{selected.subject}</h3>
                <div className="text-xs text-[#5C5B5A] mt-1 font-mono">
                  Da: {selected.from_name ? `${selected.from_name} <${selected.from_address}>` : selected.from_address}
                </div>
                <div className="text-xs text-[#8C8B8A] mt-0.5 font-mono">
                  {new Date(selected.received_at).toLocaleString("it-IT")}
                </div>
                <pre className="text-sm mt-3 whitespace-pre-wrap font-mono text-[#1C1B1A] max-h-40 overflow-y-auto bg-[#F9F8F6] p-3 border border-[#E5E4E0]">
{selected.body_text}
                </pre>
              </div>

              <div>
                <div className="flex items-center justify-between">
                  <div className="label-mono">Risposta AI</div>
                  <StatusBadge status={selected.status} />
                </div>

                <Label className="label-mono text-[10px] mt-4 block">Oggetto</Label>
                <Input
                  data-testid="reply-subject-input"
                  value={edit.ai_subject}
                  onChange={(e) => setEdit({ ...edit, ai_subject: e.target.value })}
                  className="mt-1 border-[#E5E4E0] bg-transparent focus:border-[#2C4C3B] focus:ring-0"
                />

                <Label className="label-mono text-[10px] mt-3 block">Corpo</Label>
                <Textarea
                  data-testid="reply-body-textarea"
                  rows={12}
                  value={edit.ai_reply}
                  onChange={(e) => setEdit({ ...edit, ai_reply: e.target.value })}
                  className="mt-1 border-[#E5E4E0] bg-white focus:border-[#2C4C3B] focus:ring-0 font-mono text-sm"
                />

                {selected.error && (
                  <p className="text-xs text-[#991B1B] mt-2">Errore: {selected.error}</p>
                )}

                {(selected.status === "replied" || selected.status === "drafted") && (
                  <FlagStatusPanel email={selected} onReflag={reflag} busy={busy} />
                )}

                <div className="flex flex-wrap gap-2 mt-5">
                  <Button
                    data-testid="regenerate-button"
                    disabled={busy}
                    onClick={regenerate}
                    variant="outline"
                    className="border-[#2C4C3B] text-[#2C4C3B] hover:bg-[#F0EFEA] font-mono uppercase text-xs tracking-[0.2em]"
                  >
                    <Sparkles size={14} className="mr-2" /> Rigenera
                  </Button>
                  <Button
                    data-testid="save-draft-button"
                    onClick={saveDraft}
                    variant="outline"
                    className="border-[#E5E4E0] text-[#1C1B1A] hover:bg-[#F0EFEA] font-mono uppercase text-xs tracking-[0.2em]"
                  >
                    Salva bozza
                  </Button>
                  <Button
                    data-testid="send-reply-button"
                    disabled={busy || !edit.ai_reply}
                    onClick={send}
                    className="bg-[#2C4C3B] hover:bg-[#1E3629] text-white font-mono uppercase text-xs tracking-[0.2em]"
                  >
                    <Send size={14} className="mr-2" /> Invia
                  </Button>
                  <Button
                    data-testid="skip-button"
                    onClick={skip}
                    variant="outline"
                    className="border-[#C85A32] text-[#C85A32] hover:bg-[#FDF1EB] font-mono uppercase text-xs tracking-[0.2em] ml-auto"
                  >
                    <Trash2 size={14} className="mr-2" /> Salta
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    pending: { text: "In attesa", bg: "bg-[#FBF6E9]", fg: "text-[#8B6B00]" },
    drafted: { text: "Bozza", bg: "bg-[#FDF1EB]", fg: "text-[#C85A32]" },
    replied: { text: "Risposto", bg: "bg-[#EAF2ED]", fg: "text-[#2C4C3B]" },
    skipped: { text: "Saltato", bg: "bg-[#F0EFEA]", fg: "text-[#5C5B5A]" },
    failed: { text: "Errore", bg: "bg-[#FEE2E2]", fg: "text-[#991B1B]" },
  };
  const m = map[status] || map.pending;
  return (
    <span className={`inline-block text-[10px] font-mono uppercase tracking-[0.15em] px-2 py-1 ${m.bg} ${m.fg}`}>
      {m.text}
    </span>
  );
}

function FlagStatusPanel({ email, onReflag, busy }) {
  // Only meaningful after a reply attempt was made. If neither flag is set & no error,
  // the reply was sent before flag-tracking existed → show "Stato sconosciuto".
  const ok = email.imap_flagged;
  const labelOk = email.imap_label_applied;
  const error = email.imap_flag_error;
  const tracked = ok || error;

  let tone, Icon, title, sub;
  if (!tracked) {
    tone = "bg-[#F0EFEA] border-[#E5E4E0] text-[#5C5B5A]";
    Icon = Flag;
    title = "Stato flag IMAP sconosciuto";
    sub = "L'email è stata risposta prima del tracking flag. Puoi forzare l'applicazione qui sotto.";
  } else if (ok && labelOk) {
    tone = "bg-[#EAF2ED] border-[#2C4C3B]/30 text-[#1E3629]";
    Icon = CheckCircle2;
    title = "Flag IMAP applicate";
    sub = "Stella + Risposto + etichetta personalizzata sono visibili sul tuo Gmail/Outlook.";
  } else if (ok && !labelOk) {
    tone = "bg-[#FBF6E9] border-[#D4B650]/50 text-[#8B6B00]";
    Icon = AlertTriangle;
    title = "Flag parzialmente applicate";
    sub = `Stella e "Risposto" OK. Etichetta personalizzata rifiutata: ${error || "il server non supporta custom keywords"}.`;
  } else {
    tone = "bg-[#FEE2E2] border-[#991B1B]/30 text-[#991B1B]";
    Icon = AlertTriangle;
    title = "Flag IMAP NON applicate";
    sub = error ? `Errore: ${error}` : "Il server IMAP ha rifiutato l'operazione.";
  }

  return (
    <div data-testid="flag-status-panel" className={`mt-4 border ${tone} p-3 text-xs`}>
      <div className="flex items-start gap-3">
        <Icon size={16} className="shrink-0 mt-0.5" />
        <div className="flex-1">
          <div className="font-mono uppercase tracking-[0.15em] text-[10px]">{title}</div>
          <p className="mt-1 leading-relaxed">{sub}</p>
        </div>
        <button
          type="button"
          data-testid="reflag-button"
          onClick={onReflag}
          disabled={busy}
          className="shrink-0 text-[10px] font-mono uppercase tracking-[0.15em] px-2 py-1 border border-current hover:opacity-70 disabled:opacity-40 flex items-center gap-1"
        >
          <RefreshCw size={11} /> {tracked ? "Ritenta" : "Applica"}
        </button>
      </div>
    </div>
  );
}
